import type { CSSProperties, SyntheticEvent } from 'react'
import { getVideoThumbnailUrl } from './logMedia'

// The frame at 0s is routinely black, so nudge past the start to get a picture
// rather than a blank rectangle. Same offset the eMAR stock drawer uses.
const PREVIEW_FRAME_SECONDS = 0.1

type VideoPreviewProps = {
  videoFile: string
  videoThumbnail?: string | null
  style?: CSSProperties
  testId?: string
}

/**
 * A video's still preview: the generated thumbnail when the log has a usable
 * one, otherwise a frame drawn from the video itself.
 *
 * ROST-3625: stock logs come back with `videoThumbnail` pointing at the
 * uploaded video, because nothing generates a still on that upload path. An
 * `<img>` aimed at a `.mov` draws as a broken image, and the placeholder asset
 * is a stock photo that reads as someone else's content — so fall back to the
 * video's own first frame, as VideoThumbnailComponent in the stock record
 * drawer already does.
 */
const VideoPreview = ({
  videoFile,
  videoThumbnail,
  style,
  testId
}: VideoPreviewProps) => {
  const thumbnail = getVideoThumbnailUrl(videoThumbnail)

  if (thumbnail) {
    return <img alt="" src={thumbnail} style={style} data-testid={testId} />
  }

  return (
    <video
      src={videoFile}
      muted
      style={{ objectFit: 'cover', ...style }}
      data-testid={testId}
      onLoadedData={(e: SyntheticEvent<HTMLVideoElement>) => {
        const video = e.target as HTMLVideoElement
        video.currentTime = PREVIEW_FRAME_SECONDS
      }}
    />
  )
}

export default VideoPreview
